import { lazy, ComponentType } from 'react';

// `any` here mirrors React's own `React.lazy` signature
// (`T extends ComponentType<any>`) — it's the correct constraint.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

type Factory<T extends AnyComponent> = () => Promise<{ default: T }>;

interface LazyRetryOptions {
	/** Number of retry attempts after the initial failure. */
	retries?: number;
	/** Base delay in ms between retries; grows with each attempt. */
	interval?: number;
	/**
	 * When true, a full page reload is attempted (once) if every retry fails.
	 * This recovers from stale chunk references left over after a new deploy,
	 * where the previously-hashed chunk filenames no longer exist on the server.
	 */
	reloadOnFailure?: boolean;
}

const DEFAULTS: Required<LazyRetryOptions> = {
	retries: 5,
	interval: 500,
	reloadOnFailure: true,
};

// Guards against an infinite reload loop: we only allow one automatic reload
// per chunk between successful loads.
const RELOAD_KEY_PREFIX = 'chunk-reload:';

function hasReloaded(key: string): boolean {
	try {
		return sessionStorage.getItem(RELOAD_KEY_PREFIX + key) === '1';
	} catch {
		return false;
	}
}

function markReloaded(key: string): void {
	try {
		sessionStorage.setItem(RELOAD_KEY_PREFIX + key, '1');
	} catch {
		/* sessionStorage may be unavailable (private mode, SSR) — ignore. */
	}
}

function clearReloaded(key: string): void {
	try {
		sessionStorage.removeItem(RELOAD_KEY_PREFIX + key);
	} catch {
		/* ignore */
	}
}

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Loads a dynamically-imported module with automatic retry, returning a promise
 * suitable for `React.lazy`. Retries transient import failures (network blips)
 * with a growing backoff, and — if configured — falls back to a single page
 * reload to recover from chunks orphaned by a deploy.
 */
async function retryImport<T extends AnyComponent>(
	factory: Factory<T>,
	key: string,
	options: Required<LazyRetryOptions>,
): Promise<{ default: T }> {
	let lastError: unknown;

	for (let attempt = 0; attempt <= options.retries; attempt++) {
		try {
			const module = await factory();
			// Loaded successfully — reset the reload guard so a future stale
			// chunk (after the next deploy) can trigger a reload again.
			clearReloaded(key);
			return module;
		} catch (error) {
			lastError = error;
			if (attempt < options.retries) {
				// Linear-ish backoff: 500ms, 1000ms, 1500ms, ...
				await delay(options.interval * (attempt + 1));
			}
		}
	}

	// All retries exhausted. If the chunk is likely stale from a deploy, a
	// hard reload will pull the current index.html with valid chunk URLs.
	if (options.reloadOnFailure && !hasReloaded(key)) {
		markReloaded(key);
		window.location.reload();
		// Return a never-resolving promise so Suspense keeps showing the
		// fallback until the reload takes over.
		return new Promise<{ default: T }>(() => {
			/* intentionally never resolves — page is reloading */
		});
	}

	throw lastError;
}

/**
 * Drop-in replacement for `React.lazy` that retries failed chunk imports.
 *
 *   const MyComponent = lazyRetry(() => import('./my-component'));
 *
 * @param factory The same dynamic-import factory you'd pass to `React.lazy`.
 * @param options Optional retry/backoff/reload configuration.
 */
export function lazyRetry<T extends AnyComponent>(
	factory: Factory<T>,
	options?: LazyRetryOptions,
) {
	const resolved = { ...DEFAULTS, ...options };
	// Use the factory's source as a stable-ish identity for the reload guard.
	const key = factory.toString();
	return lazy(() => retryImport(factory, key, resolved));
}

export default lazyRetry;
